import * as braze from '@braze/web-sdk';
import {
  CONTENT_TOOLS,
  MAP_EXACT,
  MAP_SURE,
  PCM_WEB,
  ROUTE_REPORTER,
} from '@shared/core-utils';
import { subscribeOnce } from '@tmx/core';
import {
  AccountsApi,
  FleetApi,
  RoutingProfileServiceApi,
} from '@tmx/data-access';
import { WebToolItem } from '@tmx/operandi';

import { BrazeCustomAttributes } from '../models/braze-custom-attributes';
import {
  accountSummarySnapshot,
  amsStoreSnapshot,
  webToolItemsSnapshot,
} from '../repositories/ams/ams.repository';
import {
  appSettingsStoreSnapshot,
  companyLicenseFeaturesSnapshot,
  companySettingsSnapshot,
} from '../repositories/app-settings/app-settings.repository';

export const updateBrazeCustomAttributes = () => {
  const brazeUser = braze.getUser();

  if (!brazeUser) {
    return;
  }

  let accountHasCopilot = false;
  let accountHasMileon = false;
  let accountHasContenttools = false;
  let accountHasPcmweb = false;
  let accountHasMapsure = false;
  let accountHasMapexact = false;
  let accountHasCopilotTrial = false;
  let accountHasCopilotTrialExpired = false;
  let daysUntilNextCopilotExpiry = 0;
  let accountHasTraffic = false;

  const { unfilteredAssetSize, theme } = amsStoreSnapshot();
  const {
    isRoutingProfileEnabled,
    isConfigurationProfileEnabled,
    isFleetPortalEnabled,
    isMapsetsEnabled,
    dataVersion,
  } = companySettingsSnapshot();
  const {
    products,
    companyCode,
    partnerId,
    partnerName,
    companyName,
    webProducts,
  } = accountSummarySnapshot();
  const webToolItems = webToolItemsSnapshot();
  const { defaultLanguage } = appSettingsStoreSnapshot();

  const accountHasRouteReporter = webProducts.some(
    ({ applicationDisplayName, internalUseOnly }) =>
      applicationDisplayName === ROUTE_REPORTER &&
      internalUseOnly !== undefined &&
      !internalUseOnly
  );

  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.COMPANY_CODE,
    companyCode
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.PARTNER_CODE,
    partnerId
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.PARTNER_NAME,
    partnerName
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.COMPANY_NAME,
    companyName
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_NUM_ASSETS,
    unfilteredAssetSize
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.DATA_REGION,
    dataVersion
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_PREFERRED_LANGUAGE,
    defaultLanguage
  );

  for (const webToolItem of webToolItems.map((item) => new WebToolItem(item))) {
    if (webToolItem.name === CONTENT_TOOLS) {
      accountHasContenttools = true;
    } else if (webToolItem.name === MAP_SURE) {
      accountHasMapsure = true;
    } else if (webToolItem.name === PCM_WEB) {
      accountHasPcmweb = true;
    } else if (webToolItem.name === MAP_EXACT) {
      accountHasMapexact = true;
    }
  }
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_CONTENTTOOLS,
    accountHasContenttools
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_PCMWEB,
    accountHasPcmweb
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_MAPSURE,
    accountHasMapsure
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_MAPEXACT,
    accountHasMapexact
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_ROUTEREPORTER,
    accountHasRouteReporter
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.DARK_MODE,
    theme === 'dark'
  );

  for (const product of products) {
    if (product.expirationDate !== null) {
      const currentDate = new Date();
      const licenseExpirationDate = new Date(product.expirationDate);
      if (!accountHasCopilotTrial) {
        accountHasCopilotTrial = true;
      }
      if (currentDate > licenseExpirationDate) {
        accountHasCopilotTrialExpired = true;
      }

      if (licenseExpirationDate > currentDate && product.applicationId === 21) {
        const daysLeftUntilExpiry = Math.ceil(
          (licenseExpirationDate.getTime() - currentDate.getTime()) /
            (1000 * 3600 * 24)
        );

        if (daysUntilNextCopilotExpiry === 0) {
          daysUntilNextCopilotExpiry = daysLeftUntilExpiry;
        } else if (daysLeftUntilExpiry < daysUntilNextCopilotExpiry) {
          daysUntilNextCopilotExpiry = daysLeftUntilExpiry;
        }
      }

      if (!accountHasCopilot && product.applicationId === 21) {
        accountHasCopilot = true;
      }

      if (!accountHasMileon && product.applicationId === 72) {
        accountHasMileon = true;
      }
    }
  }
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_COPILOT_TRIAL,
    accountHasCopilotTrial
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_COPILOT_TRIAL_EXPIRED,
    accountHasCopilotTrialExpired
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.DAYS_UNTIL_NEXT_COPILOT_EXPIRY,
    daysUntilNextCopilotExpiry
  );

  const accountHasWeather = companyLicenseFeaturesSnapshot().weather;

  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_COPILOT,
    accountHasCopilot
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_MILEON,
    accountHasMileon
  );
  brazeUser.setCustomUserAttribute(
    BrazeCustomAttributes.ACCOUNT_HAS_WEATHER,
    accountHasWeather
  );

  subscribeOnce(AccountsApi.getCompanyMobileproducts(), {
    next: (mobileProducts) => {
      for (const product of mobileProducts) {
        if (accountHasTraffic) {
          break;
        }
        for (const feature of product.features) {
          if (feature.featureId === 25) {
            accountHasTraffic = true;
            break;
          }
        }
      }
    },
    complete: () => {
      brazeUser.setCustomUserAttribute(
        BrazeCustomAttributes.ACCOUNT_HAS_TRAFFIC,
        accountHasTraffic
      );
    },
  });

  if (isFleetPortalEnabled) {
    subscribeOnce(FleetApi.getAvoidFavorSets(), {
      next: (avoidFavorSets) => {
        brazeUser.setCustomUserAttribute(
          BrazeCustomAttributes.ACCOUNT_NUM_AFSETS,
          avoidFavorSets.unfilteredSize
        );
      },
    });

    subscribeOnce(FleetApi.getPlaceSets(), {
      next: (placeSets) => {
        brazeUser.setCustomUserAttribute(
          BrazeCustomAttributes.ACCOUNT_NUM_CUSTOMPLACES,
          placeSets.unfilteredSize
        );
      },
    });

    if (isRoutingProfileEnabled) {
      subscribeOnce(RoutingProfileServiceApi.getRoutingProfiles(), {
        next: (routingProfiles) => {
          brazeUser.setCustomUserAttribute(
            BrazeCustomAttributes.ACCOUNT_NUM_VRPS,
            routingProfiles.unfilteredSize
          );
        },
      });
    }

    if (isMapsetsEnabled) {
      subscribeOnce(FleetApi.getMapSets(), {
        next: (mapSets) => {
          brazeUser.setCustomUserAttribute(
            BrazeCustomAttributes.ACCOUNT_NUM_MAPSETS,
            mapSets.unfilteredSize
          );
        },
      });
    }

    if (isConfigurationProfileEnabled) {
      subscribeOnce(FleetApi.getConfigurationProfiles(), {
        next: (configurationProfiles) => {
          brazeUser.setCustomUserAttribute(
            BrazeCustomAttributes.ACCOUNT_NUM_CONFIGPROFILES,
            configurationProfiles.unfilteredSize
          );
        },
      });
    }
  }
};
